import { Box, Grid, LinearProgress, withStyles } from "@material-ui/core"
import NCText from "../../shared/NCText"
import { useGetDriverCompositeScore } from "../../services/queries"
import { useTranslation } from "react-i18next"
import { Rating } from "@material-ui/lab"

const ScoreBar = withStyles({
  root: {
    height: 10,
    borderRadius: 5
  },
  colorPrimary: {
    backgroundColor: "#E5E7EB"
  },
  bar: {
    borderRadius: 5,
    backgroundColor: ({ value }) =>
      value >= 80 ? "#10B981" : value >= 60 ? "#F59E0B" : "#EF4444"
  }
})(LinearProgress)

const StatCard = ({ label, value, suffix = "" }) => (
  <Box
    p={2}
    borderRadius={8}
    bgcolor="#F9FAFB"
    border="1px solid #E5E7EB"
    textAlign="center"
    minWidth={120}
  >
    <NCText
      style={{
        display: "block",
        fontSize: "12px",
        color: "#6B7280",
        marginBottom: 4
      }}
    >
      {label}
    </NCText>
    <NCText style={{ fontSize: "20px", fontWeight: 600 }}>
      {value}
      {suffix}
    </NCText>
  </Box>
)

const DriverScores = ({ driverId }) => {
  const { t } = useTranslation()
  const { data, isLoading } = useGetDriverCompositeScore(driverId, {
    enabled: !!driverId
  })

  const scores = data?.data?.data || data?.data || {}

  if (isLoading) {
    return (
      <NCText style={{ color: "#9CA3AF" }}>
        {t("common.loading")}...
      </NCText>
    )
  }

  if (!scores.composite_score && scores.composite_score !== 0) {
    return (
      <NCText style={{ color: "#9CA3AF" }}>
        {t("drivers.no_score_data")}
      </NCText>
    )
  }

  const scoreOutOf100 = Math.round((scores.composite_score / 5) * 100)

  return (
    <Box>
      {/* Composite Score */}
      <Box mb={3}>
        <Box display="flex" alignItems="center" mb={1} style={{ gap: 12 }}>
          <NCText style={{ fontSize: "28px", fontWeight: 700 }}>
            {scores.composite_score}
          </NCText>
          <NCText style={{ fontSize: "14px", color: "#6B7280" }}>/ 5.0</NCText>
        </Box>
        <Box maxWidth={400}>
          <ScoreBar variant="determinate" value={scoreOutOf100} />
        </Box>

      </Box>

      {/* Breakdown */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label={t("drivers.recency_rating")}
            value={scores.recency_weighted_rating}
            suffix=" / 5"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label={t("drivers.avg_rating")}
            value={scores.average_rating}
            suffix=" / 5"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label={t("drivers.operational_score_label")}
            value={scores.operational_score}
            suffix=" / 5"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label={t("drivers.total_reviews")}
            value={scores.total_reviews}
          />
        </Grid>
      </Grid>

      <Box mt={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label={t("drivers.acceptance_rate")}
              value={scores.acceptance_rate}
              suffix="%"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label={t("drivers.cancellation_rate")}
              value={scores.cancellation_rate}
              suffix="%"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label={t("drivers.completed_trips")}
              value={scores.total_completed}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label={t("drivers.total_offered")}
              value={scores.total_offered}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Rating stars */}
      <Box mt={2} display="flex" alignItems="center" style={{ gap: 8 }}>
        <Rating
          value={scores.recency_weighted_rating || 0}
          precision={0.1}
          readOnly
        />
        <NCText style={{ fontSize: "13px", color: "#6B7280" }}>
          {t("drivers.based_on_reviews", { count: scores.total_reviews })}
        </NCText>
      </Box>
    </Box>
  )
}

export default DriverScores
